import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { EnvironmentConfig, getCurrentEnvironment, getAllEnvironments } from '../config/environments';
import { initializeFirebase } from '../config/firebaseManager';

interface EnvironmentContextType {
  currentEnvironment: EnvironmentConfig;
  availableEnvironments: EnvironmentConfig[];
  switchEnvironment: (envName: string) => void;
  isProduction: boolean;
  isDevelopment: boolean;
  isPreview: boolean;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

const ENV_STORAGE_KEY = 'glisten-admin-selected-environment';

interface EnvironmentProviderProps {
  children: ReactNode;
}

export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({ children }) => {
  // Initialize with current build-time environment or saved preference
  const getInitialEnvironment = (): EnvironmentConfig => {
    // Check localStorage for saved preference
    const savedEnv = localStorage.getItem(ENV_STORAGE_KEY);
    if (savedEnv) {
      const env = getAllEnvironments().find(e => e.name === savedEnv);
      if (env) return env;
    }
    
    // Fall back to build-time environment
    return getCurrentEnvironment();
  };

  const initialEnv = getInitialEnvironment();
  const [currentEnvironment] = useState<EnvironmentConfig>(initialEnv);
  const [availableEnvironments] = useState<EnvironmentConfig[]>(getAllEnvironments());

  // Initialize Firebase for the current environment on mount
  useEffect(() => {
    initializeFirebase(initialEnv);
  }, [initialEnv]);

  // Update localStorage when environment changes
  useEffect(() => {
    localStorage.setItem(ENV_STORAGE_KEY, currentEnvironment.name);
  }, [currentEnvironment]);

  const switchEnvironment = (envName: string) => {
    const env = availableEnvironments.find(e => e.name === envName);
    if (env) {
      // Show confirmation dialog
      const confirmed = window.confirm(
        `Switch to ${env.displayName} environment?\n\nThe page will reload to connect to the ${env.firebase.projectId} Firebase project.`
      );
      
      if (confirmed) {
        // Save selection and reload page
        localStorage.setItem(ENV_STORAGE_KEY, env.name);
        window.location.reload();
      }
    } else {
      console.error(`Environment '${envName}' not found`);
    }
  };

  const value: EnvironmentContextType = {
    currentEnvironment,
    availableEnvironments,
    switchEnvironment,
    isProduction: currentEnvironment.name === 'production',
    isDevelopment: currentEnvironment.name === 'development',
    isPreview: currentEnvironment.name === 'preview',
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

